import PropTypes from 'prop-types'
import React, { useContext, useEffect, useRef } from 'react'
import { ReplyFormSelector, ReplyFormState } from './ReplyFormSelector'
import { AuthContext } from 'components/common/auth/AuthContext'
import { ReplyButton } from 'components/review_tiles/review_reply/ReplyButton'
import { BecomeAProModal } from 'components/supplier_show/BecomeAProModal'

export interface Reply {
  id: number
  text: string
  updatedAt?: Date
}

export interface ReviewReplyProps {
  reply?: Reply
  reviewId: number
}

// ReviewReply is the authoritative control of the reply object.
// This allows the inner components to treat the reply object as immutable.
export function ReviewReply ({ reply, reviewId }: ReviewReplyProps): React.ReactElement {
  const { canReplyToReview, isOwner } = useContext(AuthContext)
  const hasReply = (reply?.text ?? '') !== ''

  const tooltipRef = useRef<HTMLSpanElement>(null)

  const authContext = useContext(AuthContext)
  const isPaidSupplierFeatureEnabled = authContext?.canaryPaidSupplier
  const canEditOrReply = (canReplyToReview || isPaidSupplierFeatureEnabled) && hasReply
  const canOnlyReply = (canReplyToReview || isPaidSupplierFeatureEnabled) && !hasReply

  useEffect(() => {
    if (tooltipRef.current === null) return

    const tooltip = new (Foundation as any).Tooltip($(tooltipRef.current))

    return () => {
      tooltip._destroy()
    }
  }, [])

  const determineInitialFormState = (): ReplyFormState => {
    if (canEditOrReply) return ReplyFormState.READY_TO_EDIT
    if (canOnlyReply) return ReplyFormState.READY_TO_REPLY
    return ReplyFormState.STATIC
  }

  const initialFormState = determineInitialFormState()

  const lockedButton = (
    <div className='locked-row'>
      <span
        ref={tooltipRef} data-tooltip title='Upgrade to Pro to reply' style={{
          display: 'inline',
          maxWidth: 'min-content',
          borderBottom: 'none'
        }} data-position='bottom' data-alignment='left'
      >
        <BecomeAProModal anchor={<ReplyButton locked>Reply</ReplyButton>} />
      </span>
    </div>
  )

  const elementId = reply?.id ?? 'new'
  const canSeeLockedButton = !canReplyToReview && isOwner && !isPaidSupplierFeatureEnabled

  if (canSeeLockedButton && !hasReply) { return <div className='review-reply'>{lockedButton}</div> }
  if (!canReplyToReview && !hasReply && !isPaidSupplierFeatureEnabled) { return <></> }

  return (
    <div id={`review-reply-${elementId}`} className='review-reply'>
      {canSeeLockedButton && lockedButton}
      <ReplyFormSelector
        reply={reply}
        reviewId={reviewId}
        initialForm={initialFormState}
      />
    </div>
  )
}

ReviewReply.propTypes = {
  reply: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string
  }),
  reviewId: PropTypes.number.isRequired
}
